<template>
  <v-container fluid class="statistics-page">
    <v-container fluid class="bg-white pb-6">
      <v-row>
        <v-col cols="12">
          <h2 class="headline font-weight-bold mb-2">Статистика</h2>
          <p class="subtitle-1 text--secondary">Статистические данные по проектам НПА и активности</p>
        </v-col>
      </v-row>

      <v-card class="mb-4" outlined>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="3">
              <v-select
                v-model="selectedType"
                :items="statTypes"
                label="Тип отчета"
                dense outlined
                @change="onTypeChange"
              ></v-select>
            </v-col>

            <v-col cols="12" md="3" v-if="needsFilter('year')">
              <v-select v-model="filters.year" :items="years" label="Год" dense outlined @change="loadData" clearable></v-select>
            </v-col>

            <v-col cols="12" md="3" v-if="needsFilter('range')">
              <v-select v-model="filters.startYear" :items="years" label="С года" dense outlined @change="loadData" clearable></v-select>
            </v-col>

            <v-col cols="12" md="3" v-if="needsFilter('range')">
              <v-select v-model="filters.endYear" :items="years" label="По год" dense outlined @change="loadData" clearable></v-select>
            </v-col>

            <v-col cols="12" md="3" v-if="needsFilter('developer')">
              <v-select v-model="filters.developerId" :items="developers" item-text="name" item-value="id" label="Разработчик" dense outlined @change="loadData" clearable></v-select>
            </v-col>

            <v-col cols="12" md="3" v-if="needsFilter('type')">
              <v-select v-model="filters.typeId" :items="projectTypes" item-text="name" item-value="id" label="Вид проекта" dense outlined @change="loadData" clearable></v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-row class="mb-4">
        <v-col class="text-right">
          <v-btn color="success" class="mr-2" @click="exportData('excel')" :loading="loading" :disabled="loading">
            <v-icon left>mdi-file-excel</v-icon> Excel
          </v-btn>
<!--           <v-btn color="error" @click="exportData('pdf')" :disabled="loading"><v-icon left>mdi-file-pdf</v-icon> PDF</v-btn>-->
        </v-col>
      </v-row>

      <v-card class="mb-4" v-if="chartConfig">
        <v-card-title class="primary white--text">{{ meta.title }}</v-card-title>
        <v-card-text>
          <div style="height: 400px; position: relative;">
            <canvas ref="chartCanvas"></canvas>
          </div>
        </v-card-text>
      </v-card>

      <v-card v-if="tableData.items.length">
        <v-card-title>Таблица данных</v-card-title>
        <v-data-table
          :headers="tableData.headers"
          :items="tableData.items"
          dense
          :items-per-page="10"
          class="elevation-1"
        >
          <template v-slot:item="{ item }">
            <tr :class="{'font-weight-bold primary lighten-4': item.isTotal}">
              <td v-for="(header, i) in tableData.headers" :key="i">
                {{ item[header.value] }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>

      <v-snackbar v-model="showError" color="error" top>
        {{ errorMsg }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="showError = false">Закрыть</v-btn>
        </template>
      </v-snackbar>

      <v-overlay :value="loading"><v-progress-circular indeterminate size="64"></v-progress-circular></v-overlay>
    </v-container>
  </v-container>
</template>

<script>
import Chart from 'chart.js/auto';
import i18n from '../plugins/i18n';

export default {
  name: 'NpaStatistics',
  data: () => ({
    loading: false,
    selectedType: 'projects_by_years',
    statTypes: [
      { text: 'Проекты НПА по годам', value: 'projects_by_years' },
      { text: 'Проекты НПА по кварталам', value: 'projects_by_quarters' },
      { text: 'Проекты НПА по разработчикам', value: 'projects_by_developers' },
      { text: 'Проекты НПА по видам', value: 'projects_by_types' },
      { text: 'Комментарии по годам', value: 'comments_by_years' },
      { text: 'Комментарии без ответов', value: 'unanswered_comments' },
      { text: 'Комментарии без ответов (по разработчикам)', value: 'unanswered_comments_by_developers' },
      { text: 'Предложения граждан', value: 'offers' }
    ],
    filters: { year: null, startYear: null, endYear: null, developerId: null, typeId: null },
    years: [],
    developers: [],
    projectTypes: [],

    chartConfig: null,
    tableData: { headers: [], items: [] },
    meta: { title: '' },

    chartInstance: null,
    errorMsg: '',
    showError: false
  }),
  mounted() {
    this.loadDicts();
    this.loadData();
  },
  beforeDestroy() {
    if (this.chartInstance) this.chartInstance.destroy();
  },
  methods: {
    needsFilter(filterName) {
      const map = {
        year: ['projects_by_quarters'],
        range: ['projects_by_years', 'projects_by_developers', 'projects_by_types', 'comments_by_years', 'unanswered_comments', 'unanswered_comments_by_developers', 'offers'],
        developer: ['projects_by_developers', 'projects_by_types', 'unanswered_comments', 'comments_by_years'],
        type: ['projects_by_types', 'projects_by_years']
      };
      return map[filterName]?.includes(this.selectedType);
    },

    onTypeChange() {
      this.filters = { year: null, startYear: null, endYear: null, developerId: null, typeId: null };
      this.chartConfig = null;
      this.tableData = { headers: [], items: [] };
      this.loadData();
    },

    async loadData() {
      if (this.selectedType === 'projects_by_developers' && !this.filters.developerId) return;

      this.loading = true;
      try {
        const { data } = await this.$http.get(`${i18n.locale}/api/statistics/${this.selectedType}`, {
          params: this.cleanFilters()
        });

        if (data.success) {
          this.chartConfig = data.chart;
          this.tableData = data.table;
          this.meta = data.meta;
          this.$nextTick(this.renderChart);
        }
      } catch (e) {
        this.errorMsg = e.response?.data?.message || 'Ошибка загрузки данных';
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },

    renderChart() {
      if (this.chartInstance) this.chartInstance.destroy();
      const ctx = this.$refs.chartCanvas?.getContext('2d');
      if (!ctx || !this.chartConfig) return;

      this.chartInstance = new Chart(ctx, {
        type: this.chartConfig.type,
        data: this.chartConfig.data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: true, position: 'top' },
            tooltip: { mode: 'index', intersect: false }
          },
          ...this.chartConfig.options
        }
      });
    },

    async exportData(format) {
      this.loading = true;
      try {
        const response = await this.$http.post(
          `${i18n.locale}/api/statistics/export/${format}`,
          { type: this.selectedType, filters: this.cleanFilters() },
          { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `statistics_${this.selectedType}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.errorMsg = 'Ошибка экспорта';
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },

    cleanFilters() {
      const res = {};
      for (const k in this.filters) if (this.filters[k]) res[k] = this.filters[k];
      return res;
    },

    async loadDicts() {
      try {
        const [y, d, t] = await Promise.all([
          this.$http.get(`${i18n.locale}/api/statistics/available-years`),
          this.$http.get(`${i18n.locale}/api/statistics/available-developers`),
          this.$http.get(`${i18n.locale}/api/statistics/available-project-types`)
        ]);
        this.years = y.data.years || [];
        this.developers = d.data.developers || [];
        this.projectTypes = t.data.types || [];
      } catch (e) { console.error('Ошибка загрузки справочников', e); }
    }
  }
};
</script>
